import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCancelOrder } from '@/hooks/useOrderActions';
import type { CancelReason } from '@/types/api';
import { cn } from '@/lib/utils';

interface CancelReasonDialogProps {
  orderId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancelled?: () => void;
}

const REASONS: CancelReason[] = ['out_of_stock', 'closing_soon', 'customer_request', 'other'];

export function CancelReasonDialog({ orderId, open, onOpenChange, onCancelled }: CancelReasonDialogProps) {
  const { t } = useTranslation();
  const cancel = useCancelOrder();
  const [picked, setPicked] = useState<CancelReason>('out_of_stock');
  const [note, setNote] = useState('');

  const submit = () => {
    const composed = note.trim() ? `${picked}: ${note.trim()}` : picked;
    cancel.mutate(
      { orderId, reason: composed },
      {
        onSuccess: () => {
          toast.success(t('orders.toasts.cancelled', { id: orderId }));
          onOpenChange(false);
          setNote('');
          onCancelled?.();
        },
        onError: (err) => toast.error(t('orders.toasts.actionFailed', { message: err.message })),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg border-border bg-background">
        <DialogHeader>
          <div className="label-tracked text-[10px]">Cancel</div>
          <DialogTitle className="font-mono text-xl font-semibold leading-tight">
            {t('cancel.title', { id: orderId })}
          </DialogTitle>
          <DialogDescription>{t('cancel.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          {REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setPicked(r)}
              className={cn(
                'rounded-md border px-4 py-3 text-left text-sm font-medium transition-all',
                picked === r
                  ? 'border-primary bg-primary/10 text-primary shadow-[inset_0_-2px_0_0_hsl(var(--primary))]'
                  : 'border-border bg-card hover:border-primary/30',
              )}
            >
              {t(`cancel.reasons.${r}`)}
            </button>
          ))}
        </div>

        <div className="space-y-1.5">
          <label className="label-tracked">{t('cancel.note')}</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={cancel.isPending}>
            {t('cancel.back')}
          </Button>
          <Button variant="destructive" size="lg" onClick={submit} disabled={cancel.isPending}>
            {t('cancel.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
